import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { Rsp } from "@repo/types";
import { Input } from "@/components/ui/input";
import { Button, Spinner } from "@heroui/react";
import { Check } from "lucide-react";
import { useUpdatePassword } from "@/service/api/api-with-uid";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";

export type ChangePassSearch = {
  session_code?: string;
};

export const Route = createFileRoute("/user/account/changePass")({
  validateSearch: (search: Record<string, unknown>): ChangePassSearch => ({
    session_code: asSearchString(search.session_code),
  }),
  component: UpdatePassword,
});

export default function UpdatePassword() {
    const { session_code: sessionCode } = getRouteApi("/user/account/changePass").useSearch();
    const [newPass, setNewPass] = useState({
        pass: "",
        conformPass: ""
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const navigate = useNavigate()
    const { updatePassword } = useUpdatePassword();

    const isPasswordValid = useMemo(() => {
        // 校验密码强度
        if (newPass.pass.length < 8) {
            return false
        }
        if (newPass.pass.match(/[a-z]/g)?.length === 0) {
            return false
        }
        if (newPass.pass.match(/[A-Z]/g)?.length === 0) {
            return false
        }
        if (newPass.pass.match(/\d/g)?.length === 0) {
            return false
        }
        if (newPass.pass.match(/[^a-zA-Z0-9]/g)?.length === 0) {
            return false
        }
        if (newPass.pass !== newPass.conformPass) {
            return false
        }
        return true
    }, [newPass.pass, newPass.conformPass])

    const handleSubmit = () => {
        setLoading(true)
        if (!isPasswordValid) {
            toast.error("密码不符合要求")
            setLoading(false)
            return
        }
        if (!sessionCode) {
            toast.error("授权失效，请重新开始")
            setLoading(false)
            return
        }

        updatePassword(newPass.pass, sessionCode).then(() => {
            navigate({ to: "/result/success", search: { type: "success", title: "密码重置成功", end: "true", op: "返回帮助中心", redirect: "/help" }, replace: true })
        }, ({ msg }: Rsp<null>) => toast.error(msg)).finally(() => setLoading(false))
    }
    return (
        <div className={"p-5"}>
            <div className={"text-3xl text-gray-800 mb-4 font-bold"}>输入新密码</div>
            <form className={"flex flex-col gap-4"} onSubmit={(e) => e.preventDefault()}>
                <Input type={"password"} value={newPass.pass} onChange={(e) => {
                    setNewPass({ ...newPass, pass: e.target.value })
                }} />
                <PasswordStrengthIndicator length={newPass.pass.length} lower={newPass.pass.match(/[a-z]/g)?.length || 0} upper={newPass.pass.match(/[A-Z]/g)?.length || 0} number={newPass.pass.match(/\d/g)?.length || 0} special={newPass.pass.match(/[^a-zA-Z0-9]/g)?.length || 0} />
                <Input type={"password"} value={newPass.conformPass}
                    onChange={(e) => setNewPass({ ...newPass, conformPass: e.target.value })}
                    onBlur={() => {
                        setError(newPass.pass !== newPass.conformPass)
                    }}
                />
                <Button className="w-full rounded-full mt-4" type="submit" isPending={loading} isDisabled={!isPasswordValid || error} onPress={handleSubmit}>
                    {({ isPending }) => (
                        <>
                            {isPending && <Spinner size="sm" color="current" />}
                            确认修改
                        </>
                    )}
                </Button>
            </form>
        </div>
    )
}


function PasswordStrengthIndicator({ length, lower, upper, number, special }: { length: number, lower: number, upper: number, number: number, special: number }) {
    return (
        <div className="flex flex-col gap-2 text-sm">
            <div className="flex flex-row gap-2"><Check color={length > 8 ? "#96d35f" : "#f00"} size={16} />密码长度至少8位</div>
            <div className="flex flex-row gap-2"><Check color={lower > 0 ? "#96d35f" : "#f00"} size={16} />密码至少包含一个字母</div>
            <div className="flex flex-row gap-2"><Check color={upper > 0 ? "#96d35f" : "#f00"} size={16} />密码至少包含一个大写字母</div>
            <div className="flex flex-row gap-2"><Check color={number > 0 ? "#96d35f" : "#f00"} size={16} />密码至少包含一个数字</div>
            <div className="flex flex-row gap-2"><Check color={special > 0 ? "#96d35f" : "#f00"} size={16} />密码至少包含一个特殊字符</div>
        </div>
    )
}
